import { IconCursorText } from '@tabler/icons-react'
import { TableColumnType } from 'antd'
import { Rule } from 'antd/lib/form'
import styled from 'styled-components'

import { TextCaptureBlock as RenderTextCaptureBlock } from '../../templateRenderer/CaptureBlocks/TextCaptureBlock'
import { TextCaptureBlock, TextDisplayBlock } from '../../types/blueprint'

import {
  BaseBlockConfig,
  Field,
  InputNumberField,
  TextAreaField,
  textTypeField,
  TypeOption,
} from './_BaseBlockConfig'

import antdTableSorter from '@/utils/antdTableSorter'

export const TextWrapper = styled.span`
  display: block;
  white-space: pre-wrap;
  max-width: 20rem; // seemed like a good width for the column
`

class Config extends BaseBlockConfig<TextCaptureBlock> {
  public override kind = 'capture' as const
  public override type = 'text' as const

  public override enableAdminOnlyVisiblity = true
  public override enableBlockToCsvColumn = true
  public override enableBlockToColumnDisplay = true

  public override blockTypeConfig: TypeOption = {
    kind: this.kind,
    value: this.type,
    label: 'Text',
    icon: IconCursorText,
    helpText: 'Simple text input',
  }

  public override blockFieldsConfig: Field[] = [
    textTypeField,
    {
      name: 'value',
      label: 'Default value',
      helpText: 'Used to provide a default value in the input',
      field: TextAreaField,
    },
    {
      name: 'characterLimit',
      label: 'Character limit',
      helpText:
        'Used to enforce a maxiumum amount of characters that can be entered',
      field: InputNumberField,
    },
  ]

  public override mapFormValueToBlock(
    block: TextCaptureBlock,
    value: string,
    returnDefaultValue?: boolean
  ): TextCaptureBlock {
    if (returnDefaultValue) {
      return block
    }

    return { ...block, value }
  }

  public override mapBlockToDisplayBlock(
    block: TextCaptureBlock
  ): TextDisplayBlock {
    const { value, ...restOfBlock } = block
    return {
      ...restOfBlock,
      content: value ?? '',
      kind: 'display',
    }
  }

  public override mapBlockToColumnDisplay(
    block: TextCaptureBlock
  ): React.ReactNode {
    return <TextWrapper>{block.value}</TextWrapper>
  }

  public override mapBlockToCsvValue(block: TextCaptureBlock): string | null {
    return block.value
  }

  public override mapBlockToColumnSort(
    block: TextCaptureBlock
  ): TableColumnType<unknown> {
    return {
      sorter: (a, b) => {
        const [aBlock, bBlock] = this.getAandBBlocks(block, a, b)

        if (!aBlock || !bBlock) return 0

        if (aBlock.kind === 'capture' && bBlock.kind === 'capture') {
          return antdTableSorter(aBlock, bBlock, 'value')
        }

        return 0
      },
    }
  }

  public override renderBlock(
    block: TextCaptureBlock,
    rules?: Rule[]
  ): React.ReactNode {
    return <RenderTextCaptureBlock block={block} rules={rules} />
  }
}

export const TextCaptureBlockConfig = new Config()
